import { useEffect, useRef, useState } from "react";

import {
  ALLOW_ALL,
  CONSENT_TTL_DAYS,
  CONSENT_VERSION,
  DENY_ALL,
  expiresAt,
  invalidReason,
  openConsentSettings,
  resetConsent,
  useConsent,
  useConsentRecord,
  useConsentRenewal,
  useConsentSettings,
  writeConsent,
  type ConsentRecord,
  type ConsentState,
} from "@/lib/consent";

const focusRing =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";

const primaryBtn = `inline-flex min-h-11 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 ${focusRing}`;
const secondaryBtn = `inline-flex min-h-11 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-accent ${focusRing}`;

const COPY = {
  en: {
    bannerTitle: "Cookies & analytics",
    outdatedTitle: "We updated how we measure",
    expiredTitle: "Please confirm your cookie choices again",
    outdatedPrefix: "Our tracking setup changed since your last decision, so we are asking again. ",
    expiredPrefix: (days: number) => `Your previous choice is older than ${days} days, so we are asking again. `,
    bannerText: "We only use cookies that are necessary to run this site. With your consent we also use Google Analytics 4 and anonymous performance measurements and — separately — load marketing cookies. You can decline both without losing any functionality.",
    privacy: "Privacy policy",
    privacyTo: "/privacy" as const,
    legal: "Legal notice (Impressum)",
    legalTo: "/legal" as const,
    settings: "Cookie settings",
    decline: "Decline all",
    accept: "Accept all",
    settingsDescription: "Choose per category which cookies OXORY may use. You can change or withdraw this at any time via the “Cookie settings” link in the footer.",
    categoriesLegend: "Cookie categories",
    necessaryTitle: "Strictly necessary",
    necessaryDescription: "Required to run the site securely. Always active.",
    analyticsTitle: "Performance & analytics",
    analyticsDescription: "Anonymous page-speed measurements (Core Web Vitals) and Google Analytics 4 (Google Ireland Ltd.) to understand page usage and enquiry journeys, with shortened IP addresses and no advertising features. Google may transfer data to the USA (EU-US Data Privacy Framework). Cookies: _ga, _ga_<ID>, up to 14 months.",
    marketingTitle: "Marketing",
    marketingDescription: "Cookies and pixels used for campaign measurement and remarketing. Nothing loads until you allow this.",
    history: "Consent history",
    noDecision: "No decision has been made yet. Once you choose, the date, version and expiry will be shown here.",
    lastDecision: "Last decision",
    consentVersion: "Consent version",
    version: "Version",
    current: "current",
    needsUpdate: "needs update to",
    status: "Status",
    validUntil: "Valid until",
    expired: "Expired — please decide again",
    outdated: "Outdated — please confirm your choices again",
    noStatus: "no decision yet",
    allowed: "allowed",
    analytics: "analytics",
    marketing: "marketing",
    allDeclined: "all optional cookies declined",
    currentStatus: "Current status",
    immediate: "Changes take effect immediately — tracking stops the moment you switch a category off.",
    policyVersion: "Policy version",
    validity: (days: number) => `a decision stays valid for ${days} days`,
    withdraw: "Withdraw decision",
    save: "Save settings",
    dateLocale: "en-GB",
  },
  tr: {
    bannerTitle: "Çerezler ve analiz",
    outdatedTitle: "Ölçüm yöntemimizi güncelledik",
    expiredTitle: "Lütfen çerez tercihlerinizi yeniden onaylayın",
    outdatedPrefix: "İzleme düzenimiz son tercihinizden bu yana değiştiği için yeniden onayınızı istiyoruz. ",
    expiredPrefix: (days: number) => `Önceki tercihiniz ${days} günden eski olduğu için yeniden onayınızı istiyoruz. `,
    bannerText: "Bu siteyi çalıştırmak için yalnızca zorunlu çerezleri kullanıyoruz. Onayınızla ayrıca Google Analytics 4 ve anonim performans ölçümleri kullanır ve ayrı olarak pazarlama çerezleri yükleriz. Her ikisini de işlev kaybı olmadan reddedebilirsiniz.",
    privacy: "Gizlilik politikası",
    privacyTo: "/tr/gizlilik" as const,
    legal: "Künye (Impressum)",
    legalTo: "/tr/kunye" as const,
    settings: "Çerez ayarları",
    decline: "Tümünü reddet",
    accept: "Tümünü kabul et",
    settingsDescription: "OXORY'nin hangi çerez kategorilerini kullanabileceğini seçin. Tercihinizi altbilgideki “Çerez ayarları” bağlantısından istediğiniz zaman değiştirebilir veya geri çekebilirsiniz.",
    categoriesLegend: "Çerez kategorileri",
    necessaryTitle: "Kesinlikle gerekli",
    necessaryDescription: "Sitenin güvenli çalışması için gereklidir. Her zaman aktiftir.",
    analyticsTitle: "Performans ve analiz",
    analyticsDescription: "Anonim sayfa hızı ölçümleri (Core Web Vitals) ve sayfa kullanımını ve talep süreçlerini anlamak için Google Analytics 4 (Google Ireland Ltd.); IP adresleri kısaltılır, reklam özellikleri kullanılmaz. Google verileri ABD'ye aktarabilir (AB-ABD Veri Gizliliği Çerçevesi). Çerezler: _ga, _ga_<ID>, en fazla 14 ay.",
    marketingTitle: "Pazarlama",
    marketingDescription: "Kampanya ölçümü ve yeniden pazarlama için kullanılan çerezler ve pikseller. İzin vermeden hiçbir şey yüklenmez.",
    history: "Onay geçmişi",
    noDecision: "Henüz bir tercih yapılmadı. Seçiminizden sonra tarih, sürüm ve geçerlilik süresi burada gösterilir.",
    lastDecision: "Son tercih",
    consentVersion: "Onay sürümü",
    version: "Sürüm",
    current: "güncel",
    needsUpdate: "şu sürüme güncellenmeli",
    status: "Durum",
    validUntil: "Geçerlilik sonu",
    expired: "Süresi doldu — lütfen yeniden karar verin",
    outdated: "Güncel değil — lütfen tercihlerinizi yeniden onaylayın",
    noStatus: "henüz karar verilmedi",
    allowed: "izin verilen",
    analytics: "analiz",
    marketing: "pazarlama",
    allDeclined: "tüm isteğe bağlı çerezler reddedildi",
    currentStatus: "Mevcut durum",
    immediate: "Değişiklikler hemen uygulanır — bir kategoriyi kapattığınız anda izleme durur.",
    policyVersion: "Politika sürümü",
    validity: (days: number) => `bir tercih ${days} gün geçerlidir`,
    withdraw: "Tercihi geri çek",
    save: "Ayarları kaydet",
    dateLocale: "tr-TR",
  },
} as const;

type ConsentLocale = keyof typeof COPY;

/**
 * Opt-in cookie banner with a granular settings modal: analytics and marketing
 * are separate opt-ins and nothing loads before the visitor allows a category.
 */
export function CookieConsent({ locale = "en" }: { locale?: ConsentLocale }) {
  const t = COPY[locale];
  const consent = useConsent();
  const renewal = useConsentRenewal();
  const record = useConsentRecord();
  const firstButtonRef = useRef<HTMLButtonElement>(null);
  const { open: settingsOpen, setOpen: setSettingsOpen } = useConsentSettings();
  const bannerOpen = consent === null && !settingsOpen;

  useEffect(() => {
    if (bannerOpen) firstButtonRef.current?.focus();
  }, [bannerOpen]);

  return (
    <>
      {bannerOpen ? (
        <div
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-description"
          onKeyDown={(event) => {
            if (event.key === "Escape") writeConsent(DENY_ALL);
          }}
          className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 p-4 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80 md:p-6"
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <h2
                id="cookie-consent-title"
                className="font-display text-base font-bold text-foreground"
              >
                {renewal === "outdated"
                  ? t.outdatedTitle
                  : renewal === "expired"
                    ? t.expiredTitle
                    : t.bannerTitle}
              </h2>
              <p id="cookie-consent-description" className="mt-1 text-sm text-muted-foreground">
                {renewal === "outdated"
                  ? t.outdatedPrefix
                  : renewal === "expired"
                    ? t.expiredPrefix(CONSENT_TTL_DAYS)
                    : ""}
                {t.bannerText}{" "}
                <a href={t.privacyTo} className="font-semibold text-foreground underline underline-offset-4">{t.privacy}</a>
                {" · "}
                <a href={t.legalTo} title="Impressum" className="font-semibold text-foreground underline underline-offset-4">{t.legal}</a>
              </p>
              <button
                type="button"
                onClick={openConsentSettings}
                className={`mt-2 inline-flex min-h-11 items-center rounded-sm text-sm font-semibold text-foreground underline underline-offset-4 hover:text-primary ${focusRing}`}
              >
                {t.settings}
              </button>
            </div>

            <div className="flex flex-wrap gap-3">
              <button ref={firstButtonRef} type="button" onClick={() => writeConsent(DENY_ALL)} className={primaryBtn}>
                {t.decline}
              </button>
              <button
                type="button"
                onClick={() => writeConsent(ALLOW_ALL)}
                className={primaryBtn}
              >
                {t.accept}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {settingsOpen ? (
        <CookieSettingsModal
          consent={consent ?? null}
          record={record}
          locale={locale}
          onClose={() => setSettingsOpen(false)}
          onSave={(value) => {
            writeConsent(value);
            setSettingsOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

function formatDate(value: string | Date, locale: ConsentLocale) {
  const date = typeof value === "string" ? new Date(value) : value;
  return date.toLocaleDateString(COPY[locale].dateLocale, { year: "numeric", month: "short", day: "numeric" });
}

function statusLabel(consent: ConsentState | null, locale: ConsentLocale) {
  const t = COPY[locale];
  if (!consent) return t.noStatus;
  const allowed = (["analytics", "marketing"] as const).filter((id) => consent[id]).map((id) => t[id]);
  return allowed.length ? `${t.allowed}: ${allowed.join(", ")}` : t.allDeclined;
}

function ConsentHistory({ record, locale }: { record: ConsentRecord | null; locale: ConsentLocale }) {
  const t = COPY[locale];
  if (!record) {
    return (
      <p className="mt-2 text-sm text-muted-foreground">
        {t.noDecision}
      </p>
    );
  }

  const reason = invalidReason(record);
  const expiry = expiresAt(record);

  return (
    <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
      <dt className="text-muted-foreground">{t.lastDecision}</dt>
      <dd className="text-foreground">{formatDate(record.decidedAt, locale)}</dd>

      <dt className="text-muted-foreground">{t.consentVersion}</dt>
      <dd className="text-foreground">
        {record.version === CONSENT_VERSION
          ? `${t.version} ${record.version} (${t.current})`
          : `${t.version} ${record.version} (${t.needsUpdate} ${CONSENT_VERSION})`}
      </dd>

      <dt className="text-muted-foreground">{t.status}</dt>
      <dd className="text-foreground">
        {!reason
          ? `${t.validUntil} ${formatDate(expiry, locale)}`
          : reason === "expired"
            ? t.expired
            : t.outdated}
      </dd>
    </dl>
  );
}

function CookieSettingsModal({
  consent,
  record,
  locale,
  onClose,
  onSave,
}: {
  consent: ConsentState | null;
  record: ConsentRecord | null;
  locale: ConsentLocale;
  onClose: () => void;
  onSave: (value: ConsentState) => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<ConsentState>(consent ?? DENY_ALL);
  const t = COPY[locale];
  const categories = [
    { id: "analytics" as const, title: t.analyticsTitle, description: t.analyticsDescription },
    { id: "marketing" as const, title: t.marketingTitle, description: t.marketingDescription },
  ];

  useEffect(() => {
    firstRef.current?.focus();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-settings-title"
        aria-describedby="cookie-settings-description"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            onClose();
            return;
          }
          if (event.key !== "Tab") return;
          const focusables = dialogRef.current?.querySelectorAll<HTMLElement>(
            'button, input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
          );
          if (!focusables?.length) return;
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          if (!first || !last) return;
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }}
        className="w-full max-w-lg rounded-lg border border-border bg-background p-6 shadow-xl"
      >
        <h2 id="cookie-settings-title" className="font-display text-lg font-bold text-foreground">
          {t.settings}
        </h2>
        <p id="cookie-settings-description" className="mt-2 text-sm text-muted-foreground">
          {t.settingsDescription}
        </p>

        <fieldset className="mt-5 space-y-4">
          <legend className="sr-only">{t.categoriesLegend}</legend>

          <label className="flex items-start gap-3 rounded-md border border-border p-3 text-sm">
            <input
              type="checkbox"
              checked
              disabled
              aria-describedby="cookie-necessary-desc"
              className="mt-0.5 size-4"
            />
            <span>
              <span className="font-semibold text-foreground">{t.necessaryTitle}</span>
              <span id="cookie-necessary-desc" className="mt-1 block text-muted-foreground">
                {t.necessaryDescription}
              </span>
            </span>
          </label>

          {categories.map((category, index) => (
            <label
              key={category.id}
              className="flex items-start gap-3 rounded-md border border-border p-3 text-sm"
            >
              <input
                ref={index === 0 ? firstRef : undefined}
                type="checkbox"
                name={category.id}
                checked={draft[category.id]}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, [category.id]: event.target.checked }))
                }
                aria-describedby={`cookie-${category.id}-desc`}
                className={`mt-0.5 size-4 ${focusRing}`}
              />
              <span>
                <span className="font-semibold text-foreground">{category.title}</span>
                <span id={`cookie-${category.id}-desc`} className="mt-1 block text-muted-foreground">
                  {category.description}
                </span>
              </span>
            </label>
          ))}
        </fieldset>

        <div className="mt-5 rounded-md border border-border bg-muted/30 p-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {t.history}
          </h3>
          <ConsentHistory record={record} locale={locale} />
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          {t.currentStatus}: <strong className="font-semibold text-foreground">{statusLabel(consent, locale)}</strong>. {t.immediate}
        </p>
        <p className="mt-1 text-xs text-muted-foreground" data-testid="consent-version-info">
          {t.policyVersion} {CONSENT_VERSION}
          {!record ? ` · ${t.validity(CONSENT_TTL_DAYS)}` : ""}
        </p>

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          {consent !== null ? (
            <button
              type="button"
              onClick={() => {
                resetConsent();
                onClose();
              }}
              className={secondaryBtn}
            >
              {t.withdraw}
            </button>
          ) : null}
          <button type="button" onClick={() => onSave(DENY_ALL)} className={secondaryBtn}>
            {t.decline}
          </button>
          <button type="button" onClick={() => onSave(draft)} className={primaryBtn}>
            {t.save}
          </button>
        </div>
      </div>
    </div>
  );
}
